 
import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';
import { MLLocalDateUtil } from 'ml-portals-util-js';
import _ from 'lodash';
import { withRetry } from 'ml-services-common-react';

const sendWithRetry = (endpoint, method, params, additionalHeaders) => withRetry(() => JsonRPCService.send(endpoint, method, params, additionalHeaders));

const getPolicyPeriodsByPublicID = (policyNumber, partition, additionalHeaders = {}) => sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
        'getPolicyPeriodsByPublicID', [policyNumber, partition], additionalHeaders);

const getFirstSetOfPolicyPeriods = (policy, additionalHeaders) => {
    if (policy.periods.length === 1) {
        policy.periods = [policy.latestPeriod];

        return policy;
    }

    if (policy.policyPeriodRequestPartitionSize_Ext < 1) {
        policy.policyPeriodRequestPartitionSize_Ext = 1;
    }

    /*
        if the partition size is less than 3, retrieve 3 periods in the initial partition,
        this allows at least the period before and after the current period to be retrieved
     */
    const initialPartitionSize = policy.policyPeriodRequestPartitionSize_Ext < 3 ? 3 : policy.policyPeriodRequestPartitionSize_Ext;

    policy.listOfBoundPeriods = policy.periods;

    const currentDate = MLLocalDateUtil.date2LocalDate(new Date());

    // right navigation will have the ability to retrieve future bound periods
    const listOfFutureBoundPeriods = _.remove(policy.listOfBoundPeriods, (period) => MLLocalDateUtil.compare(period.editEffectiveDate_ML, currentDate) > 0);

    const futureBoundPeriods = listOfFutureBoundPeriods.length > 0;
    let partition = policy.listOfBoundPeriods
        .splice(0, futureBoundPeriods ? initialPartitionSize - 1: initialPartitionSize)
        .map((item) => item.publicID);

    if (futureBoundPeriods) {
        if (listOfFutureBoundPeriods.length > 1) {
            listOfFutureBoundPeriods.sort((a, b) => {
                if (MLLocalDateUtil.isEqual(a.editEffectiveDate_ML, b.editEffectiveDate_ML)) {
                    return (new Date(a.createTime_ML) - new Date(b.createTime_ML));
                }

                return MLLocalDateUtil.compare(a.editEffectiveDate_ML, b.editEffectiveDate_ML);
            });
        }

        // get more than one future bound period if available and the current partition is less than the size allowed
        partition = _.concat(partition,
            listOfFutureBoundPeriods.splice(0, partition.length < initialPartitionSize - 1 ? initialPartitionSize - partition.length : 1)
                .map((item) => item.publicID)
        );

        if (listOfFutureBoundPeriods.length > 0) {
            policy.listOfFutureBoundPeriods = listOfFutureBoundPeriods;
        }
    }

    const latestPeriodIndexInPartition = _.findIndex(partition, (publicID) => publicID === policy.latestPeriod.publicID);

    if (latestPeriodIndexInPartition > -1) {
        partition.splice(latestPeriodIndexInPartition, 1);
    }

    return getPolicyPeriodsByPublicID(policy.policyNumber, partition, additionalHeaders).then((periodResult) => {
        policy.periods = periodResult;

        if (latestPeriodIndexInPartition > -1) {
            policy.periods.push(policy.latestPeriod);
        }

        return policy;
    });
};

/*
* Performance for policies with many bound periods is quite poor.
* Current approach to separate the retrieval of all periods into multiple requests has its flaws. Some of those requests take longer than 2 minutes.
* To improve this, we will retrieve the first set of latest bound periods. How many retrieved is controlled by the script paramter in PC.
* We will retrieve more policy periods based on the users actions on the policy summary page.
* If they are looking for more policy periods using the pagination controls, then the next set will be requested.
*/
const handlePolicyResult = (policy, additionalHeaders) => policy.isPeriodRequestPartitioningEnabled_Ext ? getFirstSetOfPolicyPeriods(policy, additionalHeaders) : policy;

export default class PolicyService {
    static createNewPolicyCancellationTransaction(
        policyNumber,
        cancellation,
        additionalHeaders = {}
    ) {
        return sendWithRetry(
            getProxiedServiceUrl('gatewayPolicy'),
            'createPolicyCancellationTransaction',
            [policyNumber, cancellation],
            additionalHeaders
        );
    }

    static createNewPolicyChangeTransaction(policyChange, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'createPolicyChangeTransaction', [policyChange], additionalHeaders);
    }

    static getPolicyPeriodsByPublicID = (policyNumber, partition, additionalHeaders = {}) => getPolicyPeriodsByPublicID(policyNumber, partition, additionalHeaders)

    static getPolicy(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicy', [policyNumber], additionalHeaders).then((policy) => handlePolicyResult(policy, additionalHeaders));
    }

    static getPolicyClaims(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyClaims', [policyNumber], additionalHeaders);
    }

    static getPolicyClaimsForAllStatuses(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyClaimsForAllStatuses_Ext', [policyNumber], additionalHeaders);
    }
    
    static getClaimsConsolidationEnabled(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'enableClaimsConsolidationForAgentPortal', [], additionalHeaders);
    }

    static getRecentlyIssuedPolicies(numberOfDays, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyIssuedPolicies', [numberOfDays], additionalHeaders);
    }

    static getRecentlyViewedPolicies(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getRecentlyViewedPolicies', [], additionalHeaders);
    }

    static getPolicySummariesForCurrentUser(additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicySummariesForCurrentUser', [], additionalHeaders);
    }

    static getPolicyBillingSummaryInfo(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyBillingSummaryInfo', [policyNumber], additionalHeaders);
    }

    static addRecentlyViewedPolicy(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'addRecentlyViewedPolicy', [policyNumber], additionalHeaders);
    }

    static getNotesForPolicy(policyNumber, additionalHeaders) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getNotesForPolicy',
            [policyNumber], additionalHeaders);
    }

    static getDocumentsForPolicy(policyNumber, additionalHeaders) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getDocumentsForPolicy',
            [policyNumber], additionalHeaders);
    }

    static createNoteForPolicy(params, additionalHeaders) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'createNoteForPolicy',
            params, additionalHeaders);
    }

    static createNewPolicyRenewalTransaction(params, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'createPolicyRenewalTransaction', params, additionalHeaders);
    }

    static getPolicyTransactionsForPolicy(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyTransactionsForPolicy', [policyNumber], additionalHeaders);
    }

    static getPolicyPeriodJobType(params, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyPeriodJobType', params, additionalHeaders);
    }

    static getAvailableMailingLocations(accountNumber, policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getAvailableMailingLocations', [accountNumber, policyNumber], additionalHeaders);
    }

    static getLatestPolicyPeriodForPolicy(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getLatestPolicyPeriodForPolicy', [policyNumber], additionalHeaders);
    }

    static getPolicySummariesFromPolicyNumbers(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicySummariesFromPolicyNumbers', [policyNumber], additionalHeaders);
    }

    static policySummaries = {};

    static getAccountPolicyDetailsByPolicyNumber_ML(data, additionalHeaders = {}) {
        const missingData = [];
        const returnPeriods = [];
        const policySummaryIds = [];

        _.forEach(data, (effectiveDate, policyNumber) => {
            const policySummaryId = policyNumber.toString().concat(',').concat(effectiveDate.year).concat('-').concat(effectiveDate.month).concat('-').concat(effectiveDate.day);

            policySummaryIds.push(policySummaryId);

            const periodSummary = PolicyService.policySummaries[policySummaryId];

            if (!periodSummary) {
                missingData.push(policyNumber);
            } else {
                returnPeriods.push(periodSummary);
            }
        });

        if (Object.keys(missingData).length) {
            const promise = sendWithRetry(getProxiedServiceUrl('gatewayPolicy'), 'getPolicySummariesByPolicyNumber', [missingData], additionalHeaders);

            return promise.then((periodSummaryData) => {
                periodSummaryData.forEach((periodData) => {
                    returnPeriods.push(periodData);

                    const policySummaryId = periodData.policyNumber.concat(',').concat(periodData.effective_ML.year).concat('-').concat(periodData.effective_ML.month).concat('-').concat(periodData.effective_ML.day);

                    PolicyService.policySummaries[policySummaryId] = periodData;
                });

                return returnPeriods;
            });
        }

        return new Promise((resolve) => {
            resolve(returnPeriods);
        });
    }

    static getPolicyDocumentsForPolicy(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyDocumentsForPolicy', [policyNumber], additionalHeaders);
    }

    static getESignDocsForGroupInviteID(docID, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getESignDocsForGroupInviteID', [docID], additionalHeaders);
    }

    static updateResendESignInviteRecipientEmail(docID, newRecipientEmail, isResend, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'updateResendESignInviteRecipientEmail', [docID, newRecipientEmail, isResend], additionalHeaders);
    }

    static updateResendESignInviteRecipientPhone(docID, newPhone, isResend, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'updateResendESignInviteRecipientPhone', [docID, newPhone, isResend], additionalHeaders);
    }

    static getPolicyProductType(policyNumber, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicy'),
            'getPolicyProductType', [policyNumber], additionalHeaders);
    }

    static setDocumentAsViewed(docId, token, additionalHeaders = {}) {
        return sendWithRetry(getProxiedServiceUrl('gatewayPolicyDocs'), 'setDocumentAsViewed',
            [docId, token], additionalHeaders)
    }
}
